/** @format */

import {ChangeEvent, useCallback, useEffect, useMemo, useRef, useState} from 'react';

import {LinearProgress} from '@mui/material';
import Alert from '@mui/material/Alert';
import Download from '@mui/icons-material/Download';
import SearchIcon from '@mui/icons-material/Search';

import {Trans, useTranslation} from 'react-i18next';

import {useNavigate, useSearchParams} from 'react-router-dom';

import {format, endOfDay} from 'date-fns';

import WideLayout from 'layouts/WideLayout';
import {ROUTES} from 'constants/ROUTES';
import Breadcrumbs from 'components/Breadcrumbs';

import {Period} from 'components/PeriodSelector/model';

import LegacyTextField from 'components/inputs/LegacyTextField';
import {PeriodSelector} from 'components/PeriodSelector';
import {usePeriodSelector} from 'components/PeriodSelector/hooks/usePeriodSelector';
import {TableVirtualized} from 'components/Table';
import {TanStackTableData} from 'components/Table/core/types';

import TertiarySelect from 'components/selects/TertiarySelect';

import {useDebounceInput} from 'helpers/hooks/useDebounceInput';

import {PERIODS, MEDIAPLAN_TYPE_OPTIONS, SEARCH_DEBOUNCE_TIME} from './constants';
import {useCampaignStatisticsData} from './hooks/useCampaignStatisticsData';
import {useGetColumns} from './hooks/useGetColumns';
import {exportCampaignStatisticsToExcel} from './helpers/exportToExcel';
import {KpiSettingsBlock} from './components/KpiSettingsBlock';
import {TotalRow} from './components/TotalRow';
import {
  BreadcrumbsContainer,
  PeriodSelectorContainer,
  ToolbarContainer,
  SearchBox,
  StyledSearchIcon,
  GoBackButton,
  GoBackIcon,
  DownloadExcelButton,
} from './styles';

const CampaignStatistics = () => {
  const [searchParams] = useSearchParams();
  const companyId = searchParams.get('companyId') || '';

  if (!companyId) {
    return (
      <WideLayout>
        <Alert severity='error'>
          <Trans>Проект не найден</Trans>
        </Alert>
      </WideLayout>
    );
  }

  return <CampaignStatisticsContent companyId={companyId} />;
};

const CampaignStatisticsContent = ({companyId}: {companyId: string}) => {
  const navigate = useNavigate();
  const {t} = useTranslation();

  const exportDataRef = useRef<TanStackTableData[]>([]);
  const [exportDataLength, setExportDataLength] = useState(0);

  const [selectedMediaplanType, setSelectedMediaplanType] = useState<string>('');

  const {inputValue: debouncedSearch, onDebounceChange} = useDebounceInput<string>(
    '',
    SEARCH_DEBOUNCE_TIME,
  );

  const periodProps = usePeriodSelector({
    defaultPeriod: 'current',
  });

  const {startDate, endDate, activePeriod, onChangePeriod, onChangeStartDate, onChangeEndDate} =
    periodProps;

  const {tableData, totals, companyName, companyMediaplanType, companyNotFound, loading, error} =
    useCampaignStatisticsData({
      companyId,
      startDate,
      endDate,
      mediaplanType: selectedMediaplanType || null,
      search: debouncedSearch,
    });

  useEffect(() => {
    if (companyMediaplanType) {
      setSelectedMediaplanType(companyMediaplanType.toLowerCase());
    } else {
      setSelectedMediaplanType('');
    }
  }, [companyId, companyMediaplanType]);

  const columns = useGetColumns();

  const handleDateRangeConfirm = (newStartDate: Date, newEndDate: Date) => {
    onChangeStartDate(newStartDate);
    onChangeEndDate(endOfDay(newEndDate));
  };

  const handleChangePeriod = (newPeriod: Period) => {
    onChangePeriod(newPeriod);
  };

  const handleGoBackButton = () => {
    navigate(`/${ROUTES.HOME}`);
  };

  const handleSearchChange = (e: ChangeEvent<HTMLInputElement>) => {
    onDebounceChange(e.currentTarget.value);
  };

  // TertiarySelect types onSelect as YalperAnalyticAttributionModelEnum but runtime value is string
  const handleMediaplanTypeChange = (value: string) => {
    setSelectedMediaplanType(value);
  };

  const handleDownloadExcel = useCallback(async () => {
    try {
      await exportCampaignStatisticsToExcel({
        data: exportDataRef.current,
        companyName: companyName || '',
        startDate: format(startDate, 'yyyy-MM-dd'),
        endDate: format(endDate, 'yyyy-MM-dd'),
      });
    } catch {
      // silent: non-critical action, no UI feedback needed
    }
  }, [companyName, startDate, endDate]);

  const mediaplanTypeOptions = useMemo(
    () =>
      MEDIAPLAN_TYPE_OPTIONS.map(opt => ({
        value: opt.value,
        text: t(opt.text, opt.text),
      })),
    [t],
  );

  const renderTotalRow = useCallback(
    () => (totals ? <TotalRow totals={totals} columns={columns} /> : null),
    [totals, columns],
  );

  if (error) {
    return (
      <WideLayout>
        <Alert severity='error'>{error.message}</Alert>
      </WideLayout>
    );
  }

  if (companyNotFound) {
    return (
      <WideLayout>
        <Alert severity='error'>
          <Trans>Проект не найден</Trans>
        </Alert>
      </WideLayout>
    );
  }

  return (
    <WideLayout>
      {loading && <LinearProgress style={{flex: 1, marginTop: 16}} />}
      <BreadcrumbsContainer>
        <Breadcrumbs
          items={[
            {href: ROUTES.INDEX, title: <Trans>Проекты</Trans>},
            {href: `/${ROUTES.HOME}`, title: companyName || '...'},
            {title: <Trans>Статистика по рекламным кампаниям</Trans>},
          ]}
        />
      </BreadcrumbsContainer>

      <PeriodSelectorContainer>
        <PeriodSelector
          startDate={startDate}
          endDate={endDate}
          activePeriod={activePeriod}
          onChangePeriod={handleChangePeriod}
          periodsModel={PERIODS}
          onDateRangeConfirm={handleDateRangeConfirm}
          allowSameDayRange
        />
        <GoBackButton onClick={handleGoBackButton}>
          <GoBackIcon fontSize='small' htmlColor='#fff' />
          <Trans>Назад</Trans>
        </GoBackButton>
      </PeriodSelectorContainer>

      {companyId && (
        <KpiSettingsBlock companyId={companyId} startDate={startDate} endDate={endDate} />
      )}

      <ToolbarContainer>
        <TertiarySelect
          value={selectedMediaplanType}
          options={mediaplanTypeOptions}
          onSelect={handleMediaplanTypeChange}
          disabled={loading}
          aria-label={t('Тип медиаплана', 'Тип медиаплана')}
          placeholder={t('Все', 'Все')}
        />

        <SearchBox>
          <LegacyTextField
            placeholder={t('Поиск по кампаниям')}
            onChange={handleSearchChange}
            slotProps={{
              input: {
                type: 'search',
              },
            }}
          />
          <StyledSearchIcon>
            <SearchIcon />
          </StyledSearchIcon>
        </SearchBox>

        <DownloadExcelButton
          variant='outlined'
          size='small'
          onClick={handleDownloadExcel}
          disabled={exportDataLength === 0}
          startIcon={<Download />}
        >
          <Trans>Скачать Excel</Trans>
        </DownloadExcelButton>
      </ToolbarContainer>

      <TableVirtualized
        columns={columns}
        data={tableData}
        defaultSortBy='spent'
        defaultOrderBy='desc'
        variant='dark'
        exportDataRef={exportDataRef}
        onExportDataChange={setExportDataLength}
        fixedFooterContent={totals ? renderTotalRow : undefined}
      />
    </WideLayout>
  );
};

export default CampaignStatistics;
